import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterProps } from "../../../types/AppTypes";
import {
  CONFIG_COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../../../constants/AppConstants";
import ConfigurationsCollaboratorsService from "../../../services/api/configurations/ConfigurationsCollaborators.service";
import FiltersService from "../../../services/api/filters/filters.service";
import { useNotification } from "../../notification.hook";
import {
  DATA_ADDED_SUCCESS_LABEL,
  DATA_UPDATED_SUCCESS_LABEL,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
} from "../../../constants/Dictionary";
import ConfigurationsExternalCollaboratorsService from "../../../services/api/configurations/ConfigurationsExternalCollaborators.service";
import {
  AddConfigExternalCollaboratorRequest,
  UpdateConfigCollaboratorRequest,
  UpdateConfigExternalCollaboratorRequest,
} from "../../../types/api/configurations.type";
import { useEffect } from "react";

const ACTIVE_RESOURCES_QUERY_KEY = "get-config-collaborators";
const ACTIVE_RESOURCES_FILTERS_QUERY_KEY = "get-filters-config-collaborators";

interface useManagementActiveResourcesProp {
  pageNumberActiveResources: number;
  debounceSearchActiveResources: string;
  filtersActiveResources: FilterProps[];
}

export const useManagementActiveResources = ({
  pageNumberActiveResources,
  debounceSearchActiveResources,
  filtersActiveResources,
}: useManagementActiveResourcesProp) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      ACTIVE_RESOURCES_QUERY_KEY,
      pageNumberActiveResources,
      debounceSearchActiveResources,
      filtersActiveResources,
    ],
    queryFn: ({ signal }) =>
      ConfigurationsCollaboratorsService.list(
        CONFIG_COLLABORATORS_PAGE_SIZE,
        pageNumberActiveResources,
        [
          ...filtersActiveResources,
          ...(debounceSearchActiveResources
            ? [{ key: "search", value: [debounceSearchActiveResources] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

export const useManagementActiveResourcesFilters = () => {
  const { notification } = useNotification();
  const {
    data: filtersData,
    isLoading: isFiltersLoading,
    isError,
  } = useQuery({
    queryKey: [ACTIVE_RESOURCES_FILTERS_QUERY_KEY],
    queryFn: FiltersService.listForConfigCollaborators,
    staleTime: RefreshReactQuery.INFINITY,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { filtersData, isFiltersLoading };
};

export const useUpdateActiveResource = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateActiveResource,
    isPending: isUpdatingCollaborator,
  } = useMutation({
    mutationKey: ["update-config-collaborator"],
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdateConfigCollaboratorRequest;
    }) => ConfigurationsCollaboratorsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVE_RESOURCES_QUERY_KEY] });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error("error updating collaborator:", error);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { updateActiveResource, isUpdatingCollaborator };
};

export const useDeleteExternalCollaborator = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const {
    mutateAsync: deleteExternalCollaborator,
    isPending: isDeletingExternalCollaborator,
  } = useMutation({
    mutationKey: ["delete-external-collaborator"],
    mutationFn: (id: number) =>
      ConfigurationsExternalCollaboratorsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVE_RESOURCES_QUERY_KEY] });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
    onError: (error) => {
      console.error(`Error deleting external collaborator ${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { deleteExternalCollaborator, isDeletingExternalCollaborator };
};

export const useAddExternalCollaborator = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const {
    mutateAsync: addExternalCollaborator,
    isPending: isAddingExternalCollaborator,
  } = useMutation({
    mutationKey: ["add-external-collaborator"],
    mutationFn: (data: AddConfigExternalCollaboratorRequest) =>
      ConfigurationsExternalCollaboratorsService.add(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVE_RESOURCES_QUERY_KEY] });
      notification.success(DATA_ADDED_SUCCESS_LABEL, undefined, "toast");
    },
  });

  return { addExternalCollaborator, isAddingExternalCollaborator };
};

export const useUpdateExternalCollaborator = () => {
  const { notification } = useNotification();
  const queryClient = useQueryClient();

  const {
    mutateAsync: updateExternalCollaborator,
    isPending: isUpdatingExternalCollaborator,
  } = useMutation({
    mutationKey: ["update-external-collaborator"],
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: UpdateConfigExternalCollaboratorRequest;
    }) => ConfigurationsExternalCollaboratorsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ACTIVE_RESOURCES_QUERY_KEY] });
      notification.success(DATA_UPDATED_SUCCESS_LABEL, undefined, "toast");
    },
  });

  return { updateExternalCollaborator, isUpdatingExternalCollaborator };
};
